import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../../context/LanguageContext';
import Logo from '../common/Logo';
import {
  styled,
  colors,
  typography,
  borderRadius,
  mixins,
  media,
  transitions
} from '../../styles';

const Nav = styled.nav`
  background: ${props => props.$scrolled ? 'rgba(10, 10, 10, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(10px)' : 'none'};
  height: 80px;
  ${mixins.flexBetween}
  padding: 0 max(2rem, calc((100vw - 1400px) / 2));
  /* Sol kenarı daha yakın yapmak için genel paddingle override */
  padding-left: clamp(0.5rem, 1.2vw, 1rem);
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 100;
  transition: ${transitions.default};
  border-bottom: ${props => props.$scrolled ? `1px solid rgba(255, 255, 255, 0.1)` : 'none'};

  /* Laptop (≤1024px) */
  ${media.lg} {
    height: 75px;
    padding: 0 1.25rem;
  }

  /* Tablet (≤768px) */
  ${media.md} {
    height: 80px;
    padding: 0 1rem;
  }

  /* Mobile (≤640px) */
  ${media.sm} {
    height: 75px;
    padding: 0 0.5rem;
  }

  /* Extra-small (≤400px) */
  @media (max-width: 400px) {
    height: 65px;
    padding: 0 0.25rem;
  }
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  padding: 0.5rem;
  transition: ${transitions.default};
  min-height: 44px;
  display: flex;
  align-items: center;

  /* Laptop (≤1024px) */
  ${media.lg} {
    padding: 0.5rem;
  }

  /* Tablet (≤768px) */
  ${media.md} {
    padding: 0.5rem;
  }

  /* Mobile (≤640px) */
  ${media.sm} {
    padding: 0.35rem;
  }

  /* Extra-small (≤400px) */
  @media (max-width: 400px) {
    padding: 0.25rem;
  }
`;

const MobileMenuButton = styled.button`
  /* Tüm varsayılan button stillerini sıfırla */
  all: unset;
  
  /* Kendi stillerimizi uygula */
  display: none;
  color: ${colors.white};
  font-size: 28px;
  cursor: pointer;
  z-index: 102;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 48px;
  height: 48px;
  ${mixins.flexCenter}
  position: relative;
  
  /* Kesinlikle şeffaf arka plan */
  background: none;
  background-color: transparent;
  border: none;
  outline: none;
  box-shadow: none;
  
  /* Pseudo-elementler için de sıfırla */
  &::before,
  &::after {
    display: none;
  }

  /* İkon geçiş animasyonu */
  & > * {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
  }

  /* Hover efekti - sadece opacity */
  &:hover {
    opacity: 0.7;
  }

  /* Aktif durumda sadece küçülme */
  &:active {
    transform: scale(0.9);
  }

  /* Büyük ekranlarda gizle */
  @media (min-width: 769px) {
    display: none !important;
  }

  /* Sadece tablet ve mobil cihazlarda göster */
  ${media.md} {
    display: flex;
    margin-left: 0.5rem;
  }
`;

const NavMenu = styled.div`
  ${mixins.flexCenter}
  gap: 2rem;

  /* Büyük ekranlarda her zaman göster */
  @media (min-width: 769px) {
    display: flex !important;
    position: static;
    background: none;
    backdrop-filter: none;
    flex-direction: row;
    justify-content: center;
    padding: 0;
    z-index: auto;
    height: auto;
    overflow-y: visible;
  }

  ${media.md} {
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 15, 15, 0.98);
    backdrop-filter: blur(10px);
    flex-direction: column;
    justify-content: center;
    padding: 5rem 2rem;
    z-index: 101;
    gap: 2.5rem;
    height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    
    /* Animasyon özellikleri */
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-20px)')};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    
    /* Menü öğeleri için staggered animasyon */
    & > * {
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
      transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(20px)')};
    }
    
    /* Her öğe için farklı gecikmeler */
    & > *:nth-child(1) {
      transition-delay: ${({ $isOpen }) => ($isOpen ? '0.1s' : '0s')};
    }
    & > *:nth-child(2) {
      transition-delay: ${({ $isOpen }) => ($isOpen ? '0.2s' : '0s')};
    }
    & > *:nth-child(3) {
      transition-delay: ${({ $isOpen }) => ($isOpen ? '0.3s' : '0s')};
    }
  }

  ${media.sm} {
    padding: 4rem 1.5rem;
    gap: 2rem;
  }
`;

const SocialLinks = styled.div`
  ${mixins.flexCenter}
  gap: 1.5rem;

  ${media.md} {
    gap: 2rem;
    margin-bottom: 1.5rem;
  }

  ${media.sm} {
    gap: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const SocialLink = styled.a`
  color: ${colors.white};
  font-size: ${typography.sizes.lg};
  transition: ${transitions.default};
  width: 48px;
  height: 48px;
  ${mixins.flexCenter}
  border-radius: ${borderRadius.sm};
  
  &:hover {
    color: ${colors.primary};
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  ${media.md} {
    font-size: ${typography.sizes.xl};
    width: 52px;
    height: 52px;
    
    &:hover {
      transform: scale(1.1);
    }
  }

  ${media.sm} {
    width: 44px;
    height: 44px;
    font-size: ${typography.sizes.lg};
  }
`;

const LanguageSwitch = styled.div`
  ${mixins.flexCenter}
  gap: 0.75rem;
`;

const LanguageButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$active ? colors.white : 'rgba(255, 255, 255, 0.5)'};
  font-size: ${typography.sizes.base};
  cursor: pointer;
  padding: 0.75rem;
  border-radius: ${borderRadius.sm};
  transition: ${transitions.default};
  width: 48px;
  height: 48px;
  ${mixins.flexCenter}

  &:hover {
    color: ${colors.white};
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: linear-gradient(135deg, #ff2d72, #E91E63);
  }

  ${media.sm} {
    width: 44px;
    height: 44px;
    padding: 0.5rem;
    font-size: ${typography.sizes.sm};
  }
`;

const Divider = styled.span`
  color: rgba(255, 255, 255, 0.3);
  padding: 0 0.25rem;
`;

const ContactButton = styled(Link)`
  ${mixins.flexCenter}
  padding: 0.75rem 1.5rem;
  background: linear-gradient(145deg, #ff2d72, #c91854);
  color: ${colors.white};
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: ${borderRadius.full};
  text-decoration: none;
  font-weight: ${typography.weights.semibold};
  transition: ${transitions.default};
  height: 48px;
  min-width: 120px;
  font-size: ${typography.sizes.base};
  box-shadow: 
    8px 8px 20px rgba(0, 0, 0, 0.5),
    -6px -6px 15px rgba(255, 255, 255, 0.08),
    inset 0 1px 1px rgba(255, 255, 255, 0.3),
    inset 0 -1px 1px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 
      10px 10px 25px rgba(0, 0, 0, 0.6),
      -8px -8px 20px rgba(255, 255, 255, 0.1),
      inset 0 1px 1px rgba(255, 255, 255, 0.3),
      inset 0 -1px 1px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 
      inset 4px 4px 10px rgba(0, 0, 0, 0.4),
      inset -4px -4px 10px rgba(255, 255, 255, 0.1);
  }

  ${media.md} {
    padding: 0.75rem 2rem;
    font-size: ${typography.sizes.lg};
    height: 52px;
    min-width: 140px;
  }

  ${media.sm} {
    padding: 0.75rem 1.5rem;
    font-size: ${typography.sizes.base};
    height: 44px;
    min-width: 110px;
  }
`;

// SVG ikonları aynı kalabilir
const SocialIcons = {
  linkedin: (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path fill="currentColor" d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path fill="currentColor" d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path fill="currentColor" d="M10,15L15.19,12L10,9V15M21.56,7.17C21.69,7.64 21.78,8.27 21.84,9.07C21.91,9.87 21.94,10.56 21.94,11.16L22,12C22,14.19 21.84,15.8 21.56,16.83C21.31,17.73 20.73,18.31 19.83,18.56C19.36,18.69 18.5,18.78 17.18,18.84C15.88,18.91 14.69,18.94 13.59,18.94L12,19C7.81,19 5.2,18.84 4.17,18.56C3.27,18.31 2.69,17.73 2.44,16.83C2.31,16.36 2.22,15.73 2.16,14.93C2.09,14.13 2.06,13.44 2.06,12.84L2,12C2,9.81 2.16,8.2 2.44,7.17C2.69,6.27 3.27,5.69 4.17,5.44C4.64,5.31 5.5,5.22 6.82,5.16C8.12,5.09 9.31,5.06 10.41,5.06L12,5C16.19,5 18.8,5.16 19.83,5.44C20.73,5.69 21.31,6.27 21.56,7.17Z" />
    </svg>
  )
};

const MobileNavigation = styled.nav`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    gap: 1.5rem;
    a { color: white; text-decoration: none; }
    a:hover, a:focus-visible { text-decoration: underline; }
  }
`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage, translations } = useContext(LanguageContext);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = event => { if (event.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', onKeyDown); };
  }, [isOpen]);

  return (
    <Nav $scrolled={scrolled}>
      <LogoLink to="/" onClick={() => setIsOpen(false)}>
        <Logo 
          src="/images/logo.png" 
          height="60px"
          mobileHeight="48px"
          fontSize="2rem"
          mobileFontSize="1.5rem"
        />
      </LogoLink>
      <MobileMenuButton 
        onClick={() => setIsOpen(!isOpen)} 
        aria-expanded={isOpen}
        aria-controls="main-menu"
        aria-label={language === 'tr' ? (isOpen ? 'Menüyü Kapat' : 'Menüyü Aç') : (isOpen ? 'Close menu' : 'Open menu')}
      >
        <span style={{ 
          display: 'inline-block',
          width: '24px',
          height: '24px',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
        }}>
          {isOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </span>
      </MobileMenuButton>
      <NavMenu id="main-menu" $isOpen={isOpen}>
        <MobileNavigation aria-label={language === 'tr' ? 'Sayfalar' : 'Pages'}>
          <Link to="/" onClick={() => setIsOpen(false)}>{language === 'tr' ? 'Ana Sayfa' : 'Home'}</Link>
          <Link to="/#apps" onClick={() => setIsOpen(false)}>{language === 'tr' ? 'Uygulamalar' : 'Apps'}</Link>
        </MobileNavigation>
        <SocialLinks>
          <SocialLink 
            href="https://www.linkedin.com/company/creanity" 
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            {SocialIcons.linkedin}
          </SocialLink>
          <SocialLink 
            href="https://instagram.com/creanity_" 
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            {SocialIcons.instagram}
          </SocialLink>
          <SocialLink 
            as="span"
            style={{ opacity: 0.5, cursor: 'not-allowed' }}
            aria-label="YouTube (Yakında)"
            aria-disabled="true"
          >
            {SocialIcons.youtube}
          </SocialLink>
        </SocialLinks>
        <LanguageSwitch>
          <LanguageButton 
            onClick={() => setLanguage('tr')} 
            $active={language === 'tr'}
            aria-label="Türkçe"
            aria-pressed={language === 'tr'}
          >
            TR
          </LanguageButton>
          <Divider>|</Divider>
          <LanguageButton 
            onClick={() => setLanguage('en')} 
            $active={language === 'en'}
            aria-label="English"
            aria-pressed={language === 'en'}
          >
            EN
          </LanguageButton>
        </LanguageSwitch>
        <ContactButton to="/contact" onClick={() => setIsOpen(false)}>
          {translations[language].contact}
        </ContactButton>
      </NavMenu>
    </Nav>
  );
};

export default Navbar; 